import { useCallback, useMemo, type MouseEvent } from "react";
import { Clock } from "lucide-react";
import Avatar from "../../../../../../../components/shared/Avatar";
import Checkbox from "../../../../../../../components/shared/inputs/Checkbox";
import { useCompanyCurrency } from "../../../../../../../utils/hooks/company";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import { useMenuContext } from "../../../../hooks/context/useMenuContext";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import { renderDurationAsFormat } from "../../../../../../../utils/helpers/date";
import { currencyFormatter } from "../../../../../../../utils/helpers/currencyFormatter";
import type { Event } from "../../../../types/Event";

interface ResourceCellProps {
    resource: any;
    column: any;
    rowHeight: number;
}

function ResourceCell({ resource, rowHeight }: ResourceCellProps) {
    const currency = useCompanyCurrency();
    const [{ mode }] = useFilters();
    const { openMenu } = useMenuContext();
    const { columns, ctrlShiftS, events, dispatch, selectedEvents, type } = useScheduleContext();
    const isTeamScheduler = ["team", "template", "profile"].includes(type);
    const { canViewRates } = usePermissions();

    const style = useMemo(() => {
        let value: any = { height: `${rowHeight}px` };
        const visibleColumns = Object.values(columns || {})?.filter((item) => item);
        if (visibleColumns.length < 1) value.borderRight = 0;
        return value;
    }, [columns, rowHeight]);

    const handleResourceContextMenu = useCallback((e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "my-profile") return;
        const rect = e.currentTarget.getBoundingClientRect();
        openMenu({ id: "resource-menu", x: rect.right - 50, y: rect.top + 35, resource });
    }, [openMenu, resource, type]);

    const rowEvents = useMemo(() => {
        return events.filter((event: Event) => event.resourceId === resource.id);
    }, [events, resource.id]);

    const isChecked = useMemo(() => {
        const allSelected = rowEvents.length > 0 && rowEvents.every((event: Event) =>
            selectedEvents.some((item: Event) => item.id === event.id)
        );
        return !!allSelected;
    }, [selectedEvents, rowEvents]);

    const onChange = useCallback(() => {
        const ids = rowEvents.map((item: any) => item.id);
        const payload = isChecked
            ? selectedEvents.filter((event: Event) => !ids.includes(event.id))
            : [...selectedEvents, ...rowEvents];
        dispatch({ type: "select-events", payload });
    }, [rowEvents, selectedEvents, isChecked, dispatch]);

    const disabled = useMemo(() => {
        const hasUnassignedEvents = selectedEvents.find(
            (event: Event) => event.resourceId === "unassigned-shifts"
        );
        return !!hasUnassignedEvents || rowEvents.length === 0;
    }, [selectedEvents, rowEvents.length]);

    const { overtime, total, regular, breaks } = (rowEvents || []).reduce(
        (t: any, event: any) => {
            t.total += parseFloat(event.total || "0");
            (event.sections || []).forEach((section: any) => {
                if (section.type === "overtime") {
                    t.overtime += parseFloat(section.duration || "0");
                } else if (section.type === "break") {
                    t.overtime += parseFloat(section.duration || "0");
                } else {
                    t.regular += parseFloat(section.duration || "0");
                }
            });
            return t;
        },
        { overtime: 0, total: 0, regular: 0, breaks: 0 },
    );

    const duration = renderDurationAsFormat(overtime + regular + breaks, "HH:mm");
    const showCost = canViewRates || !isTeamScheduler;

    return (
        <div
            className="employee-info-cell tw-cursor-pointer"
            onContextMenu={handleResourceContextMenu}
            style={style}
        >
            <div className="tw-flex tw-items-center tw-gap-x-2.5 tw-w-60">
                {(mode !== "day" && ctrlShiftS && type !== "my-profile") && (
                    <Checkbox
                        id={resource?.id}
                        className="tw-bg-white tw-shrink-0"
                        checked={isChecked}
                        onCheckedChange={onChange}
                        disabled={disabled}
                    />
                )}
                <Avatar user={resource}  />
                <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
                    <span className="tw-text-sm tw-font-semibold tw-text-neutral-800 tw-truncate tw-leading-tight tw-mb-1">
                        {resource.name}
                    </span>
                    <div className="tw-flex tw-items-center tw-gap-1">
                        <div className="tw-flex tw-items-center tw-gap-1 tw-text-neutral-500">
                            <Clock size={9} />
                            <span className="tw-text-xs tw-leading-none">{duration}</span>
                        </div>
                        {showCost && total > 0 && (
                            <>
                                <span className="tw-px-1 tw-text-neutral-400">• </span>
                                <span className="tw-text-xs tw-text-primary tw-font-medium tw-leading-none">
                                    {currencyFormatter(total, currency)}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResourceCell;
